<template>
  <div>
    <div class="flex justify-between items-center">
      <div class="flex items-center gap-1 min-w-0" style="width: 60%">
        <DepartmentIcon v-if="inDiffDept" :department="crewMember.department" />
        <TruncateText class="font-bold" :text="crewMember.full_name" />
      </div>
      <div class="flex items-center gap-2">
        <Checkbox
          v-model="useCrewLeaderTimes"
          label="Use Tech's Time"
          class="whitespace-nowrap"
        />
        <div class="border-l border-gray-300 h-5"></div>
        <button
          type="button"
          class="flex items-center gap-1 text-sm font-bold text-blue-600 hover:text-blue-800 whitespace-nowrap"
          @click="removeCrewMember"
        >
          <svg
            class="w-3 h-3"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          Remove
        </button>
      </div>
    </div>
    <ActualTimesFormV2
      v-if="!useCrewLeaderTimes"
      class="mt-2"
      :appointment-status="appointment.status"
      :name="timesFormName"
      :accepted-time="formData.accepted_time"
      :dispatch-time="formData.dispatch_time"
      :arrival-time="formData.arrival_time"
      :departure-time="formData.departure_time"
      :scheduled-time="appointment.scheduled_time"
      :data-vv-as="timesFormName"
      :appt-pre-work-enabled="appointment.pre_work_enabled"
      @change="handleTimeChange"
    />
    <div class="border-b border-gray-200 my-2"></div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import { getProperty } from "@/lib/helpers";

import DepartmentIcon from "@/components/DepartmentIcon.vue";
import Checkbox from "@/components/Seranova/Checkbox/Checkbox.vue";
import TruncateText from "@/components/TruncateText.vue";
import ActualTimesFormV2 from "@/EditAppointmentModal/forms/ActualTimesFormV2.vue";

const props = defineProps<{
  crewMember: Record<string, unknown>;
  appointment: Record<string, unknown>;
}>();

const emit = defineEmits<{
  change: [payload: Record<string, unknown>];
  "remove-crew-member": [member: Record<string, unknown>];
}>();

const store = useStore();

const useCrewLeaderTimes = ref(
  props.crewMember.use_crew_leader_times as boolean
);

const formData = reactive({
  use_crew_leader_times: useCrewLeaderTimes.value,
  accepted_time: props.crewMember.accepted_time,
  dispatch_time: props.crewMember.dispatch_time,
  arrival_time: props.crewMember.arrival_time,
  departure_time: props.crewMember.departure_time
});

const inDiffDept = (() => {
  const jobDept = (store.state as Record<string, unknown>).job as
    | Record<string, unknown>
    | undefined;
  return (
    getProperty(jobDept?.department, "id") !==
    getProperty(props.crewMember.department, "id")
  );
})();

const timesFormName = (() => {
  const rowKey = props.crewMember?.index ?? props.crewMember?.id ?? "row";
  return `crew-${rowKey}`;
})();

const crewPayload = () => ({
  ...props.crewMember,
  ...formData
});

watch(useCrewLeaderTimes, (newValue) => {
  formData.use_crew_leader_times = newValue;
  if (!newValue) {
    formData.arrival_time = props.appointment.arrival_time;
    formData.dispatch_time = props.appointment.dispatch_time;
    formData.departure_time = props.appointment.departure_time;
  }
  emit("change", crewPayload());
});

const handleTimeChange = (name: string, value: string) => {
  (formData as Record<string, unknown>)[name] = value;
  emit("change", crewPayload());
};

const removeCrewMember = () => {
  emit("remove-crew-member", props.crewMember);
};
</script>
